import { ADMIN_ROLE } from "@sperrdatei/dto/roles";
import { useContext, useEffect, useRef, useState } from "react";
import { useBlocker, useParams, useSearchParams } from "react-router";

import { ApiContext } from "../../api-context";
import { ItemList } from "../../components/item-list";
import { Modal } from "../../components/modal";
import { ToastContext } from "../../components/toast";
import { UserList } from "../../components/user-list";
import { UserSessionContext } from "../../use-user-context";
import { withResponse } from "../../utils/with-response";

import { DomainModal } from "./domain-modal";
import { ServiceUserModal } from "./service-user-modal";
import { UserModal } from "./user-modal";

import type { ForwardFormRef } from "./shared";
import type {
  User,
  InstituteRequest,
  ApiError,
  InstituteResponse,
  ServiceUser,
} from "@sperrdatei/dto/types";
import type { FormEvent } from "react";

const NoAdminError = {
  code: "ORG_HAS_NO_ADMIN",
  message: "Das Institut muss mindestens einen Institut-Administrator haben",
};

const DomainInUseError = {
  code: "DOMAIN_IN_USE",
  message: "Sie können keine Domain löschen, die einem Benutzer zugeordnet ist",
};

const isUserEqual = (user1: User, user2: User): boolean => {
  if (user1.firstName !== user2.firstName) {
    return false;
  }

  if (user1.lastName !== user2.lastName) {
    return false;
  }

  const isEqualSet = (xs: Set<string>, ys: Set<string>): boolean =>
    xs.size === ys.size && [...xs].every((x) => ys.has(x));

  return isEqualSet(new Set(user1.roles), new Set(user2.roles));
};

export const Institute: React.FC = () => {
  const { apiFetch } = useContext(ApiContext);
  const [userSession] = useContext(UserSessionContext);
  const toast = useContext(ToastContext);

  const { action } = useParams<{ action: "add" | "edit" }>();
  const [searchParams] = useSearchParams();

  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isBlockerModalOpen, setIsBlockerModalOpen] = useState(false);
  const [isAddDomainModalOpen, setIsAddDomainModalOpen] = useState(false);
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);
  const [isAddServiceUserModalOpen, setIsAddServiceUserModalOpen] =
    useState(false);
  const [validated, setValidated] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<ApiError | undefined>();

  const [domains, setDomains] = useState<string[]>([]);
  const [primaryDomain, setPrimaryDomain] = useState<string | undefined>(
    undefined,
  );
  const [selectedDomain, setSelectedDomain] = useState<string | undefined>();
  const [users, setUsers] = useState<User[]>([]);
  const [serviceUsers, setServiceUsers] = useState<ServiceUser[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | undefined>();
  const [selectedServiceUser, setSelectedServiceUser] = useState<
    ServiceUser | undefined
  >();

  const userFormRef = useRef<ForwardFormRef<User>>(null);
  const serviceUserFormRef = useRef<ForwardFormRef<ServiceUser>>(null);
  const domainFormRef = useRef<ForwardFormRef<string>>(null);

  const isFormValid = name && domains.length && users.length;
  const isAdmin =
    userSession?.active &&
    userSession.roles.some((role) => role === ADMIN_ROLE.key);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      hasPendingChanges && currentLocation.pathname !== nextLocation.pathname
    );
  });

  useEffect(() => {
    if (action === "add") {
      clearForm();
    }
  }, [action]);

  useEffect(() => {
    if (blocker.state === "blocked") {
      setIsBlockerModalOpen(true);
    }
  }, [blocker]);

  useEffect(() => {
    const loadOrganization = async (instituteId?: string): Promise<void> => {
      const requesturl = instituteId
        ? `/api/admin/institute/${instituteId}`
        : `/api/user/institute`;
      const response = await apiFetch(requesturl);
      withResponse<InstituteResponse[200], undefined>(response, {
        onSuccess: async (institute) => {
          setName(institute.name);
          setDomains(institute.domains.map((domain) => domain.domainName));
          setUsers(institute.users);
          setServiceUsers(institute.serviceUsers);
          setPrimaryDomain(institute.primaryDomain);

          return undefined;
        },
        onFailure: async (error) => {
          return error;
        },
      });
    };

    if (action === "add") {
      return;
    }

    const institutId = searchParams.get("institutId") ?? undefined;
    loadOrganization(institutId);
  }, [action, apiFetch, searchParams, userSession]);

  const clearForm = (): void => {
    setName("");
    setDomains([]);
    setUsers([]);
    setServiceUsers([]);
    setHasPendingChanges(false);
    setValidated(false);
  };

  const getRequestUrl = (institutId: string | null): string => {
    if (isAdmin) {
      if (action === "add") {
        return "/api/admin/institute";
      }

      return institutId
        ? `/api/admin/institute/${institutId}`
        : "/api/user/institute";
    }
    return "/api/user/institute";
  };

  const isUserUnique = (username: string, domain: string): boolean => {
    return ![...users, ...serviceUsers].some(
      (u) => u.username === username && u.domain === domain,
    );
  };

  const getUpdatedUserIndex = <T extends User | ServiceUser>(
    selectedUser: T | undefined,
    userList: T[],
  ): number => {
    if (!selectedUser) {
      return -1;
    }

    return userList.findIndex(
      (u) =>
        u.username === selectedUser?.username &&
        u.domain === selectedUser?.domain,
    );
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    setError(undefined);
    e.preventDefault();
    setValidated(true);
    setHasPendingChanges(false);

    if (!isFormValid) {
      return;
    }

    const hasAdmin = users.some((u) => u.roles.includes("INSTITUTE_ADMIN"));

    if (!hasAdmin) {
      setError(NoAdminError);
      return;
    }

    const organizationRequest: InstituteRequest = {
      name,
      domains,
      users,
      serviceUsers,
    };

    const institutId = searchParams.get("institutId");
    const response = await apiFetch(getRequestUrl(institutId), {
      method: action === "add" ? "POST" : "PUT",
      body: JSON.stringify(organizationRequest),
    });

    await withResponse(response, {
      onSuccess: async () => {
        toast?.showToast({
          caption: "Änderungen gespeichert",
          message: `Die Änderungen am Institut wurden gespeichert.`,
        });
        setValidated(false);
        setHasPendingChanges(false);
      },
      onFailure: async (error) => {
        setError(error);

        return error;
      },
    });
  };

  return (
    <div>
      <Modal
        isOpen={isBlockerModalOpen}
        onClose={() => setIsBlockerModalOpen(false)}
        title="Ungespeicherte Änderungen"
        primaryAction={{
          label: "Weiter ohne Speichern",
          buttonStyle: "danger",
          onClick: () => {
            if (blocker.state === "blocked") {
              blocker.proceed();
            }
          },
        }}
        secondaryAction={{
          label: "Zurück",
          onClick: async () => {
            if (blocker.state === "blocked") {
              blocker.reset();
            }
            setIsBlockerModalOpen(false);
          },
        }}
      >
        <p>
          Sie haben ungespeicherte Änderungen. Wollen Sie die Seite wirklich
          verlassen?
        </p>
      </Modal>

      <DomainModal
        domainFormRef={domainFormRef}
        selectedDomain={selectedDomain}
        isAddDomainModalOpen={isAddDomainModalOpen}
        setIsAddDomainModalOpen={setIsAddDomainModalOpen}
        handleAddDomainSubmit={(): void => {
          if (domainFormRef.current?.validate()) {
            const domain = domainFormRef.current.getResult();
            const mode = domainFormRef.current.getMode();
            if (mode === "INSERT") {
              const oldLength = domains.length;
              const newDomains = Array.from(new Set([...domains, domain]));
              setDomains(newDomains);
              if (oldLength !== newDomains.length) {
                setHasPendingChanges(true);
              }
            } else {
              const idx = domains.findIndex((d) => d === selectedDomain);
              if (idx >= 0) {
                if (domains[idx] !== domain) {
                  domains[idx] = domain;
                  setDomains(Array.from(new Set([...domains])));
                  setHasPendingChanges(true);
                }
              }
            }
            setIsAddDomainModalOpen(false);
            setSelectedDomain(undefined);
          }
        }}
      />

      <UserModal
        userFormRef={userFormRef}
        selectedUser={selectedUser}
        setSelectedUser={(user) => setSelectedUser(user)}
        domains={domains}
        isAddUserModalOpen={isAddUserModalOpen}
        setIsAddUserModalOpen={setIsAddUserModalOpen}
        handleAddUserSubmit={(): void => {
          if (userFormRef.current?.validate()) {
            const user = userFormRef.current.getResult();
            const mode = userFormRef.current.getMode();
            if (mode === "INSERT") {
              if (isUserUnique(user.username, user.domain)) {
                setHasPendingChanges(true);
                setUsers([...users, user]);
                setIsAddUserModalOpen(false);
              } else {
                userFormRef.current.setError(
                  "Der Benutzername wird bereits verwendet",
                );
              }
            }
            if (mode === "EDIT") {
              const idx = getUpdatedUserIndex(selectedUser, users);
              if (idx >= 0) {
                if (!isUserEqual(users[idx], user)) {
                  setHasPendingChanges(true);
                  users[idx] = user;
                  setUsers([...users]);
                }
              }
              setIsAddUserModalOpen(false);
            }
          }
        }}
      />

      <ServiceUserModal
        userFormRef={serviceUserFormRef}
        selectedUser={selectedServiceUser}
        setSelectedUser={(user) => setSelectedServiceUser(user)}
        domain={primaryDomain}
        isAddUserModalOpen={isAddServiceUserModalOpen}
        setIsAddUserModalOpen={setIsAddServiceUserModalOpen}
        handleAddUserSubmit={(): void => {
          if (serviceUserFormRef.current?.validate()) {
            const user = serviceUserFormRef.current.getResult();
            const mode = serviceUserFormRef.current.getMode();
            if (mode === "INSERT") {
              if (isUserUnique(user.username, user.domain)) {
                setHasPendingChanges(true);
                setServiceUsers([...serviceUsers, user]);
                setIsAddServiceUserModalOpen(false);
              } else {
                serviceUserFormRef.current.setError(
                  "Der Benutzername wird bereits verwendet",
                );
              }
            }
            if (mode === "EDIT") {
              setIsAddServiceUserModalOpen(false);
            }
          }
        }}
      />

      <h3 className="page-caption mb-3">
        Institut {action === "add" ? "anlegen" : "bearbeiten"}
      </h3>
      <form
        onSubmit={handleSubmit}
        className="default-form container ms-0 mt-2 g-0 w-100"
        noValidate
      >
        {error &&
          error.code !== "DOMAIN_IN_USE" &&
          error.code !== "ORG_HAS_NO_ADMIN" && (
            <div className="alert alert-danger mb-3" role="alert">
              <h6 className="alert-heading small">
                <i className="bi bi-exclamation-triangle-fill" /> Fehler
              </h6>
              <span className="small">{error.message}</span>
            </div>
          )}
        <div className="mb-3">
          <label htmlFor="instituteName" className="form-text">
            Name
          </label>
          <input
            type="text"
            id="instituteName"
            value={name}
            disabled={!isAdmin || !searchParams.get("institutId")}
            className={
              !name
                ? `form-control ${validated ? "is-invalid" : ""}`
                : `form-control ${validated ? "is-valid" : ""}`
            }
            onChange={(e) => {
              setName(e.target.value);
              setHasPendingChanges(true);
            }}
            required
          />
          <div className="invalid-feedback">Angabe notwendig</div>
        </div>

        <div className="mb-3">
          <ItemList
            caption="Domänen"
            items={domains}
            addItem={() => setIsAddDomainModalOpen(true)}
            disabled={!isAdmin || !searchParams.get("institutId")}
            removeItem={(domain: string): void => {
              if (users.some((user) => user.domain === domain)) {
                setError(DomainInUseError);
                return;
              }

              setDomains(domains.filter((d) => d !== domain));
              setHasPendingChanges(true);
            }}
            editItem={(item) => {
              setIsAddDomainModalOpen(true);
              setSelectedDomain(item);
            }}
          />
          <div
            className="invalid-feedback"
            style={
              error && error.code === "DOMAIN_IN_USE"
                ? { display: "block" }
                : {}
            }
          >
            {error?.message}
          </div>
        </div>
        <div className="mb-3">
          <UserList
            caption="Benutzer"
            users={users}
            disabled={domains.length === 0}
            addUser={() => setIsAddUserModalOpen(true)}
            editUser={(user) => {
              setIsAddUserModalOpen(true);
              setSelectedUser(user);
            }}
            removeUser={(user) => {
              const hasAdmin = users
                .filter((u) => u !== user)
                .some((u) => u.roles.includes("INSTITUTE_ADMIN"));
              if (!hasAdmin) {
                setError(NoAdminError);
                return;
              }
              setHasPendingChanges(true);
              setUsers(users.filter((u) => u !== user));
            }}
          />
          <div
            className="invalid-feedback"
            style={
              error && error.code === "ORG_HAS_NO_ADMIN"
                ? { display: "block" }
                : {}
            }
          >
            {error?.message}
          </div>
        </div>
        <div className="mb-3">
          <UserList
            caption="Technische Benutzer"
            users={serviceUsers}
            disabled={domains.length === 0}
            addUser={() => setIsAddServiceUserModalOpen(true)}
            editUser={(user) => {
              setIsAddServiceUserModalOpen(true);
              setSelectedServiceUser(user);
            }}
            removeUser={(user) => {
              setHasPendingChanges(true);
              setServiceUsers(serviceUsers.filter((u) => u !== user));
            }}
          />
        </div>

        <button
          type="submit"
          className="mt-3 btn btn-primary"
          disabled={!isFormValid || !hasPendingChanges}
        >
          {action === "add" ? "Erstellen" : "Speichern"}
        </button>
      </form>
    </div>
  );
};
